import type { JSX } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Alert, Button, Empty, Skeleton, Tag } from 'antd';
import {
  ArrowLeftOutlined,
  CheckCircleFilled,
  LinkOutlined,
  QuestionCircleOutlined,
  ReloadOutlined,
} from '@ant-design/icons';

import { decisionTreeApi, type DecisionEdge, type DecisionNode } from '../lib/wikiApi';
import { safeHttpUrl } from './opening-tasks/stage-tasks';

/**
 * Интерактивный renderer decision tree (Q75-78).
 *
 * Граф (DecisionTree.graph) ведёт пользователя пошагово:
 *   - kind:'question' — показываем текст вопроса + кнопки-ответы (edges).
 *   - kind:'action'   — терминальная инструкция (зелёный блок), без edges.
 *   - kind:'link'     — узел-ссылка: edges с articleSlug/url открывают переход.
 *
 * Edge может вести:
 *   - nextNodeId  → следующий узел внутри дерева;
 *   - articleSlug → открыть статью (leaf, reachedLeaf=true);
 *   - url         → внешняя ссылка (leaf, reachedLeaf=true).
 *
 * Breadcrumb позволяет вернуться назад (Q76 — неограниченная глубина).
 * Путь логируется на backend при достижении листа (Q78 — аналитика выборов).
 */
interface HistoryStep {
  nodeId: string;
  /** Какой ответ выбрали, уходя с этого узла (для breadcrumb). */
  chosenLabel: string | null;
}

export function DecisionTreeRenderer({ slug }: { slug: string }): JSX.Element {
  const navigate = useNavigate();

  const { data: graph, isLoading, error } = useQuery({
    queryKey: ['wiki', 'decision-tree', slug],
    queryFn: () => decisionTreeApi.get(slug),
    enabled: !!slug,
  });

  // История пройденных узлов (последний = текущий).
  const [history, setHistory] = useState<HistoryStep[]>([]);
  // Накопленный путь из меток-выборов для аналитики.
  const [pathLog, setPathLog] = useState<string[]>([]);
  const [loggedLeaf, setLoggedLeaf] = useState(false);

  const nodeById = useMemo(() => {
    const m = new Map<string, DecisionNode>();
    if (graph) for (const n of graph.nodes) m.set(n.id, n);
    return m;
  }, [graph]);

  const currentId = history.length > 0 ? history[history.length - 1].nodeId : graph?.rootId;
  const current = currentId ? nodeById.get(currentId) : undefined;
  const isTerminal = !!current && (!current.edges || current.edges.length === 0);

  // Логирование достижения листа (терминальный узел) — в эффекте, не в render.
  useEffect(() => {
    if (!current || !isTerminal || loggedLeaf) return;
    setLoggedLeaf(true);
    const finalPath = [...pathLog, `«${current.text.slice(0, 40)}»`];
    decisionTreeApi.logPath(slug, finalPath, true).catch(() => {
      /* аналитика не критична */
    });
  }, [current, isTerminal, loggedLeaf, pathLog, slug]);

  const restart = useCallback((): void => {
    setHistory([]);
    setPathLog([]);
    setLoggedLeaf(false);
  }, []);

  const goBack = useCallback((): void => {
    setHistory((h) => h.slice(0, -1));
    setPathLog((p) => p.slice(0, -1));
    setLoggedLeaf(false);
  }, []);

  const handleEdge = useCallback(
    (edge: DecisionEdge): void => {
      if (!graph) return;
      const label = edge.label || 'Далее';
      if (edge.nextNodeId && nodeById.has(edge.nextNodeId)) {
        setHistory((h) => {
          const copy = h.length > 0 ? [...h] : [{ nodeId: graph.rootId, chosenLabel: null }];
          copy[copy.length - 1] = { ...copy[copy.length - 1], chosenLabel: label };
          copy.push({ nodeId: edge.nextNodeId!, chosenLabel: null });
          return copy;
        });
        setPathLog((p) => [...p, label]);
        return;
      }
      // Лист: статья или внешняя ссылка — логируем достижение и переходим.
      if (edge.articleSlug) {
        if (!loggedLeaf) {
          setLoggedLeaf(true);
          decisionTreeApi.logPath(slug, [...pathLog, `${label} → статья`], true).catch(() => {});
        }
        navigate(`/article/${encodeURIComponent(edge.articleSlug)}`);
        return;
      }
      // Только http(s): `javascript:` из сценария не открываем (аудит 19.10.2026).
      const url = safeHttpUrl(edge.url);
      if (url) {
        if (!loggedLeaf) {
          setLoggedLeaf(true);
          decisionTreeApi.logPath(slug, [...pathLog, `${label} → ссылка`], true).catch(() => {});
        }
        window.open(url, '_blank', 'noopener,noreferrer');
      }
    },
    [graph, nodeById, navigate, slug, pathLog, loggedLeaf],
  );

  if (isLoading) return <Skeleton active paragraph={{ rows: 4 }} />;
  if (error) {
    return (
      <Alert
        type="error"
        showIcon
        message="Не удалось загрузить сценарий"
        description={(error as Error).message}
      />
    );
  }
  if (!graph || !graph.nodes?.length) {
    return <Empty description="Интерактивный сценарий ещё не настроен" style={{ padding: '40px 0' }} />;
  }
  if (!current) {
    return (
      <Alert
        type="warning"
        showIcon
        message="Сценарий повреждён"
        description="Не найден стартовый узел. Откройте редактор сценария и пересоберите дерево."
      />
    );
  }

  const accent = 'var(--brasol-accent)';

  return (
    <div
      style={{
        border: '1px solid var(--border-color, #e8e6f0)',
        borderRadius: 8,
        padding: 24,
        background: '#fff',
        boxShadow: '0 1px 3px rgba(37,29,74,0.06)',
      }}
    >
      {/* Breadcrumb пройденного пути */}
      {history.length > 0 && (
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: 6,
            marginBottom: 16,
            fontSize: 12,
            color: 'var(--text-muted)',
          }}
        >
          <Button
            size="small"
            type="text"
            icon={<ArrowLeftOutlined />}
            onClick={goBack}
            style={{ color: accent, fontWeight: 600 }}
          >
            Назад
          </Button>
          {history
            .filter((s) => s.chosenLabel)
            .map((s, i) => (
              <span key={`${s.nodeId}-${i}`} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <span>›</span>
                <Tag style={{ margin: 0 }} color="default">
                  {s.chosenLabel}
                </Tag>
              </span>
            ))}
        </div>
      )}

      {/* Текущий узел */}
      {isTerminal ? (
        <div
          style={{
            display: 'flex',
            gap: 12,
            alignItems: 'flex-start',
            padding: '16px 18px',
            background: 'rgba(124, 179, 66, 0.08)',
            border: '1px solid var(--brasol-success, #7cb342)',
            borderRadius: 6,
          }}
        >
          <CheckCircleFilled style={{ color: 'var(--brasol-success, #7cb342)', fontSize: 22, marginTop: 2 }} />
          <div>
            <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 4 }}>Итог</div>
            <div style={{ fontSize: 15, lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>{current.text}</div>
          </div>
        </div>
      ) : (
        <div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', marginBottom: 18 }}>
            <QuestionCircleOutlined style={{ color: accent, fontSize: 22, marginTop: 3 }} />
            <div style={{ fontWeight: 700, fontSize: 18, lineHeight: 1.4, whiteSpace: 'pre-wrap' }}>
              {current.text}
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {current.edges.map((edge, i) => {
              const isLink = !!edge.articleSlug || !!edge.url;
              return (
                <Button
                  key={i}
                  size="large"
                  block
                  onClick={() => handleEdge(edge)}
                  icon={isLink ? <LinkOutlined /> : undefined}
                  style={{
                    height: 'auto',
                    minHeight: 48,
                    textAlign: 'left',
                    whiteSpace: 'normal',
                    justifyContent: 'flex-start',
                    fontWeight: 600,
                    borderColor: isLink ? 'var(--brasol-info)' : accent,
                    color: isLink ? 'var(--brasol-info)' : 'var(--text-primary, #251D4A)',
                    padding: '10px 16px',
                  }}
                >
                  {edge.label || (isLink ? 'Открыть' : 'Далее')}
                </Button>
              );
            })}
          </div>
        </div>
      )}

      {/* Управление: начать заново */}
      {(history.length > 0 || isTerminal) && (
        <div style={{ marginTop: 18, textAlign: 'center' }}>
          <Button type="text" icon={<ReloadOutlined />} onClick={restart} style={{ color: 'var(--text-muted)' }}>
            Начать сценарий заново
          </Button>
        </div>
      )}
    </div>
  );
}
